import { useState, useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Link from '@mui/material/Link'
import Pagination from '@mui/material/Pagination'
import CircularProgress from '@mui/material/CircularProgress'
import { useMediaQuery } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { Search as SearchIcon } from '@mui/icons-material'
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import {
  useNotifications,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
} from 'services/api/notification/notificationHooks'

const UNREAD_DOT_COLOR = '#2666BE'
const UNREAD_BG = '#E6F0FF'
const EMPTY_ARRAY = []

const resolveNotificationRoute = (item) => {
  const detailData = item?.detailData || {}
  const type = item?.type

  if (type === 'Task') {
    return `/app/tasks-dashboard/my-tasks`
  }

  if (type === 'Leave') {
    return `/app/approval-center/leave/pending`
  }

  if (type === 'Claim') {
    return `/app/approval-center/claim/pending`
  }

  if (type === 'Payroll') {
    return `/app/payroll/dashboard`
  }

  // if (type === 'Appraisal') {
  //   return `/app/tasks-dashboard/project-tracker` // KIV
  // }

  if (type === 'Attendance') {
    return `/app/attendance`
  }

  return '/app/notifications'
}

const TaskIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M9 11L12 14L22 4" stroke="#2666BE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="#2666BE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const LeaveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 2V6M8 2V6M3 10H21" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ClaimIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="#F3BF3A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const PayrollIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M8.212 15.637L4.5 16.167L5.03 12.455L14.576 2.90901C14.998 2.48705 15.5703 2.25 16.167 2.25C16.7637 2.25 17.336 2.48705 17.758 2.90901C18.18 3.33097 18.417 3.90327 18.417 4.50001C18.417 5.09675 18.18 5.66905 17.758 6.09101L8.212 15.637Z" stroke="#B028B7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.25 8.25H2.25C1.85218 8.25 1.47064 8.40804 1.18934 8.68934C0.908035 8.97064 0.75 9.35218 0.75 9.75V20.25C0.75 20.6478 0.908035 21.0294 1.18934 21.3107C1.47064 21.592 1.85218 21.75 2.25 21.75H21.75C22.1478 21.75 22.5294 21.592 22.8107 21.3107C23.092 21.0294 23.25 20.6478 23.25 20.25V9.75C23.25 9.35218 23.092 8.97064 22.8107 8.68934C22.5294 8.40804 22.1478 8.25 21.75 8.25H20.25M20.25 18H12.75M20.25 14.25H15.75" stroke="#B028B7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const AppraisalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <g clipPath="url(#clip0_3696_4732)">
      <path d="M2.00167 17.8692C1.73645 17.8692 1.4821 17.9746 1.29456 18.1621C1.10702 18.3496 1.00167 18.604 1.00167 18.8692V20.3692C1.00167 20.4355 1.02801 20.4991 1.07489 20.546C1.12177 20.5929 1.18536 20.6192 1.25167 20.6192H4.75167C4.81797 20.6192 4.88156 20.5929 4.92844 20.546C4.97533 20.4991 5.00167 20.4355 5.00167 20.3692V18.8692C5.00167 18.604 4.89631 18.3496 4.70877 18.1621C4.52124 17.9746 4.26688 17.8692 4.00167 17.8692H2.00167ZM8.00167 14.8692C7.73645 14.8692 7.4821 14.9746 7.29456 15.1621C7.10702 15.3496 7.00167 15.604 7.00167 15.8692V20.3692C7.00167 20.4355 7.02801 20.4991 7.07489 20.546C7.12177 20.5929 7.18536 20.6192 7.25167 20.6192H10.7517C10.818 20.6192 10.8816 20.5929 10.9284 20.546C10.9753 20.4991 11.0017 20.4355 11.0017 20.3692V15.8692C11.0017 15.604 10.8963 15.3496 10.7088 15.1621C10.5212 14.9746 10.2669 14.8692 10.0017 14.8692H8.00167ZM14.0017 11.3692C13.7364 11.3692 13.4821 11.4746 13.2946 11.6621C13.107 11.8496 13.0017 12.104 13.0017 12.3692V20.3692C13.0017 20.4355 13.028 20.4991 13.0749 20.546C13.1218 20.5929 13.1854 20.6192 13.2517 20.6192H16.7517C16.818 20.6192 16.8816 20.5929 16.9284 20.546C16.9753 20.4991 17.0017 20.4355 17.0017 20.3692V12.3692C17.0017 12.104 16.8963 11.8496 16.7088 11.6621C16.5212 11.4746 16.2669 11.3692 16.0017 11.3692H14.0017ZM19.2517 20.6192H22.7517C22.818 20.6192 22.8816 20.5929 22.9284 20.546C22.9753 20.4991 23.0017 20.4355 23.0017 20.3692V8.36921C23.0017 8.10399 22.8963 7.84964 22.7088 7.6621C22.5212 7.47457 22.2669 7.36921 22.0017 7.36921H20.0017C19.7365 7.36921 19.4821 7.47457 19.2946 7.6621C19.107 7.84964 19.0017 8.10399 19.0017 8.36921V20.3692C19.0017 20.4355 19.028 20.4991 19.0749 20.546C19.1218 20.5929 19.1854 20.6192 19.2517 20.6192ZM18.0717 4.20921L18.3817 4.66921C18.4496 4.77127 18.5418 4.85485 18.65 4.91245C18.7582 4.97005 18.8791 4.99986 19.0017 4.99921C19.129 4.9946 19.2529 4.95719 19.3616 4.89062C19.4702 4.82405 19.5598 4.73056 19.6217 4.61921L21.6217 1.11921C21.6952 1.00057 21.7341 0.863774 21.7341 0.724209C21.7341 0.584645 21.6952 0.447849 21.6217 0.329209C21.5478 0.21157 21.4414 0.117821 21.3154 0.059267C21.1895 0.000713166 21.0492 -0.0201446 20.9117 -0.000790854L16.9117 0.499209C16.7849 0.515237 16.6644 0.563368 16.5615 0.639039C16.4586 0.71471 16.3767 0.815414 16.3236 0.931601C16.2705 1.04779 16.248 1.17561 16.2581 1.30295C16.2683 1.43028 16.3108 1.55291 16.3817 1.65921L16.6817 2.11921C16.7001 2.14589 16.713 2.17604 16.7195 2.20783C16.726 2.23962 16.7259 2.2724 16.7194 2.30418C16.7128 2.33596 16.6999 2.36608 16.6814 2.39273C16.6629 2.41938 16.6392 2.44199 16.6117 2.45921L2.45167 11.9192C2.34174 11.9919 2.24726 12.0855 2.17364 12.1948C2.10003 12.3041 2.04874 12.4268 2.02272 12.556C1.9967 12.6852 1.99646 12.8182 2.02202 12.9475C2.04759 13.0767 2.09844 13.1997 2.17167 13.3092C2.2634 13.4449 2.38706 13.556 2.53178 13.6327C2.67651 13.7095 2.83786 13.7495 3.00167 13.7492C3.19827 13.7515 3.39062 13.692 3.55167 13.5792L17.7217 4.13921C17.7491 4.12051 17.7801 4.10748 17.8126 4.10087C17.8452 4.09426 17.8788 4.09422 17.9114 4.10074C17.944 4.10725 17.9749 4.1202 18.0024 4.13883C18.03 4.15745 18.0535 4.18138 18.0717 4.20921ZM1.00167 23.9992H23.0017C23.2393 23.9987 23.4689 23.9135 23.6495 23.7591C23.8301 23.6046 23.9497 23.3909 23.987 23.1562C24.0243 22.9215 23.9769 22.6813 23.8531 22.4784C23.7293 22.2756 23.5374 22.1234 23.3117 22.0492C23.2116 22.0163 23.107 21.9994 23.0017 21.9992H1.00167C0.896345 21.9994 0.791722 22.0163 0.691666 22.0492C0.465928 22.1234 0.273986 22.2756 0.150233 22.4784C0.0264801 22.6813 -0.0209965 22.9215 0.0163103 23.1562C0.0536171 23.3909 0.173269 23.6046 0.353827 23.7591C0.534385 23.9135 0.764048 23.9987 1.00167 23.9992Z" fill="#2A7471" />
    </g>
    <defs>
      <clipPath id="clip0_3696_4732">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
)

const AttendanceIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
    <path d="M11 21C16.523 21 21 16.523 21 11C21 5.477 16.523 1 11 1C5.477 1 1 5.477 1 11C1 16.523 5.477 21 11 21Z" stroke="#AF4C4C" strokeWidth="2" strokeLinejoin="round" />
    <path d="M11.0039 5V11.005L15.2434 15.245" stroke="#AF4C4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const typeConfig = {
  task: {
    label: 'Task',
    Icon: TaskIcon,
    iconBg: '#DEECFF',
  },
  leave: {
    label: 'Leave Request',
    Icon: LeaveIcon,
    iconBg: '#E8F5E9',
  },
  claim: {
    label: 'Claim',
    Icon: ClaimIcon,
    iconBg: '#FBF5E2',
  },
  payroll: {
    label: 'Payroll',
    Icon: PayrollIcon,
    iconBg: '#E8E2FB',
  },
  appraisal: {
    label: 'Appraisal',
    Icon: AppraisalIcon,
    iconBg: '#E2F8FB',
  },
  attendance: {
    label: 'Attendance',
    Icon: AttendanceIcon,
    iconBg: '#F5E8E8',
  },
}

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'task', label: 'Tasks' },
  { value: 'leave', label: 'Leave Requests' },
  { value: 'claim', label: 'Claims' },
  { value: 'payroll', label: 'Payrolls' },
  { value: 'appraisal', label: 'Appraisals' },
  { value: 'attendance', label: 'Attendance' },
]

function NotificationRow({ item, onClick }) {
  const normalizedType = item?.type?.toLowerCase?.() || 'task'
  const config = typeConfig[normalizedType] || typeConfig.task
  const Icon = config.Icon
  const isDescriptionLong = (item?.description || '').length > 80

  return (
    <Box
      onClick={() => onClick?.(item)}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: { xs: 1.5, sm: 2.5 },
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 },
        bgcolor: item.isUnread ? UNREAD_BG : 'common.white',
        borderBottom: '1px solid',
        borderColor: 'grey.200',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        '&:hover': {
          bgcolor: item.isUnread ? 'rgba(230, 240, 255, 0.85)' : 'grey.50',
        },
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      <Box
        sx={{
          width: { xs: 36, sm: 56 },
          height: { xs: 36, sm: 56 },
          borderRadius: '50%',
          bgcolor: config.iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.2,
            mb: 0.75,
            fontSize: { xs: '1rem', sm: '1.05rem' },
          }}
        >
          {config.label}
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 400,
            color: 'text.primary',
            lineHeight: 1.35,
            wordBreak: 'break-word',
            fontSize: { xs: '0.95rem', sm: '1rem' },
          }}
        >
          {item.description}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-end', sm: 'flex-start' },
          gap: 1,
          flexShrink: 0,
          flexDirection: { xs: 'column', sm: 'row' },
          pl: { xs: 0.5, sm: 2 },
          ml: 'auto',
          minWidth: { xs: 72, sm: 'auto' },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.2,
            mt: { xs: 0, sm: 0.25 },
            whiteSpace: { xs: isDescriptionLong ? 'normal' : 'nowrap', sm: 'nowrap' },
            textAlign: 'right',
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
          }}
        >
          {item.timeAgo}
        </Typography>

        {item.isUnread && (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: UNREAD_DOT_COLOR,
              mt: { xs: 0.2, sm: 0.6 },
            }}
          />
        )}
      </Box>
    </Box>
  )
}

export default function Notifications() {
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState('all')
  const [page, setPage] = useState(1)

  const navigate = useNavigate()

  const isLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'))
  const headerHeight = isLarge ? 91 : 60

  const { data, isLoading, isFetching } = useNotifications({
    page,
    perPage: 20,
    search,
    type: activeTab,
  })

  const markAllReadMutation = useMarkAllNotificationsAsRead()
  const markOneReadMutation = useMarkNotificationAsRead()

  const notifications = data?.items ?? EMPTY_ARRAY

  const unreadCountsByTab = useMemo(() => {
    const counts = {
      all: 0,
      task: 0,
      leave: 0,
      claim: 0,
      payroll: 0,
      appraisal: 0,
      attendance: 0,
    }

    notifications.forEach((notification) => {
      const type = notification?.type?.toLowerCase?.()
      if (!notification?.isUnread) return

      counts.all += 1

      if (Object.prototype.hasOwnProperty.call(counts, type)) {
        counts[type] += 1
      }
    })

    return counts
  }, [notifications])

  const filtered = useMemo(() => {
    let list = notifications

    if (activeTab !== 'all') {
      list = list.filter((n) => n.type?.toLowerCase?.() === activeTab)
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter((n) => n.description?.toLowerCase()?.includes(q))
    }

    return list
  }, [notifications, activeTab, search])

  const total = data?.total || 0
  const lastPage = data?.lastPage || 1
  const unreadCount = unreadCountsByTab.all

  const handleMarkAllRead = () => {
    markAllReadMutation.mutate()
  }

  const handleNotificationClick = (item) => {
    const targetRoute = resolveNotificationRoute(item)

    if (item.isUnread) {
      markOneReadMutation.mutate(item.id, {
        onSuccess: () => {
          navigate(targetRoute)
        },
        onError: () => {
          navigate(targetRoute)
        },
      })
      return
    }

    navigate(targetRoute)
  }

  return (
    <DashboardContainer headerTitle="All Notifications">
      <Box
        sx={{
          height: `calc(100vh - ${headerHeight}px)`,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: {
            xs: '100%',
            sm: '100%',
            md: 860,
            lg: 1000,
            xl: 1200,
          },
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 0 },
          minHeight: 0,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 500,
                  color: 'text.primary',
                  fontSize: { xs: '1.2rem', sm: '1.5rem' },
                }}
              >
                All Notifications
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{
                  color: 'text.secondary',
                  mt: 0.25,
                  fontWeight: 400,
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                }}
              >
                You have {unreadCount} unread notifications
              </Typography>
            </Box>

            {unreadCount > 0 && (
              <Link
                component="button"
                variant="body2"
                onClick={handleMarkAllRead}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'alternate.main',
                  fontWeight: 500,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  alignSelf: { xs: 'flex-start', sm: 'center' },
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <g clipPath="url(#clip0_3221_22191)">
                    <path d="M11.9987 4L4.66536 11.3333L1.33203 8" stroke="#2666BE" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14.668 6.66602L9.66797 11.666L8.66797 10.666" stroke="#2666BE" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_3221_22191">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <Typography variant="subtitle1">Mark all as read</Typography>
              </Link>
            )}
          </Box>

          <Box
            sx={{
              mb: 3,
              p: { xs: '12px', sm: '17px' },
              bgcolor: 'common.white',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: '14px',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search notifications..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'common.white',
                  borderRadius: '10px',
                },
              }}
            />
          </Box>
        </Box>

        <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              pr: { xs: 0, sm: 0.5 },
              pb: 4,
            }}
          >
          <Box
            sx={{
              borderRadius: '14px',
              overflow: 'hidden',
              bgcolor: 'common.white',
              border: '1px solid',
              borderColor: 'grey.200',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              minHeight: 240,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 2, sm: 4 },
                flexWrap: 'wrap',
                px: { xs: 2, sm: 3 },
                pt: { xs: 2, sm: 2.5 },
                bgcolor: 'common.white',
                borderBottom: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              {TABS.map((tab) => {
                const isActive = activeTab === tab.value

                return (
                  <Box
                    key={tab.value}
                    onClick={() => {
                      setActiveTab(tab.value)
                      setPage(1)
                    }}
                    sx={{
                      pb: { xs: 1.25, sm: 1.75 },
                      borderBottom: '3px solid',
                      borderColor: isActive ? 'alternate.main' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? 'alternate.main' : 'text.secondary',
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                      }}
                    >
                      {tab.label}
                      {(unreadCountsByTab[tab.value] || 0) > 0 ? ` (${unreadCountsByTab[tab.value]})` : ''}
                    </Typography>
                  </Box>
                )
              })}
            </Box>

            {isLoading ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center', bgcolor: 'common.white' }}>
                <CircularProgress size={28} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', bgcolor: 'common.white' }}>
                <Typography variant="body2" color="text.secondary">
                  No notifications found.
                </Typography>
              </Box>
            ) : (
              filtered.map((item) => (
                <NotificationRow
                  key={item.id}
                  item={item}
                  onClick={handleNotificationClick}
                />
              ))
            )}
          </Box>

          {lastPage > 1 && filtered.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pr: 1 }}>
              <Pagination
                page={page}
                count={lastPage}
                color="primary"
                onChange={(_, value) => setPage(value)}
                disabled={isFetching}
              />
            </Box>
          )}

          {isFetching && !isLoading && (
            <Box sx={{ mt: 1, pb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Refreshing notifications...
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </DashboardContainer>
  )
}
